// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import { type TableViewId, type TableViewType, useEvolveDelete, useEvolveGet } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { isNil } from 'helpers/isNotNil';
import { parseUserPreferenceValueJson } from 'hooks/typeCacheHooks/useUserPreferences/utils';
import { getLocalStorageWrapped, setLocalStorageWrapped } from 'hooks/useLocalStorage';

/**
 * Hook to get the initial table view when loading a given table, with caching and background updates.
 * */
export const useInitialTableView = (tableViewType: TableViewType) => {
  const { apiCall: getDefaultTableViewIdApiCall } = useEvolveGet('admin/userPreference/:preferenceKey');
  const { apiCall: getTableViewApiCall } = useEvolveGet('admin/tableView/:tableViewId');
  const { apiCall: deleteDefaultTableViewIdApiCall } = useEvolveDelete('admin/userPreference/:preferenceKey');

  const [loadingInitialTableView, setLoadingInitialTableView] = useState(false);

  const getDefaultTableViewId = useCallback(
    async () =>
      await getDefaultTableViewIdApiCall({
        url: `admin/userPreference/DEFAULT_TABLE_VIEW_ID_${tableViewType}`,
      })
        .then((userPreference) => {
          if (isNil(userPreference)) return null;
          return parseUserPreferenceValueJson<`DEFAULT_TABLE_VIEW_ID_${TableViewType}`>(userPreference.preferenceValue);
        })
        .catch(() => null), // Assume the user has no default table view.
    [tableViewType, getDefaultTableViewIdApiCall],
  );

  const getTableView = useCallback(
    async (tableViewId: TableViewId) =>
      await getTableViewApiCall({
        url: `admin/tableView/${tableViewId}`,
      }).catch(() => null), // Assume the table view doesn't exist.
    [getTableViewApiCall],
  );

  const getDefaultTableView = useCallback(async () => {
    const defaultTableViewId = await getDefaultTableViewId();
    if (!defaultTableViewId) return null;
    return await getTableView(defaultTableViewId);
  }, [getDefaultTableViewId, getTableView]);

  const handleBackgroundUpdateCache = useCallback(async () => {
    const cachedDefaultTableView = getLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
    if (isNil(cachedDefaultTableView)) return;
    const latestDefaultTableViewId = await getDefaultTableViewId();
    if (!latestDefaultTableViewId) {
      // [Stale cache] User has removed their default table view preference.
      setLocalStorageWrapped(`USER_PREFERENCE_DEFAULT_TABLE_VIEW_ID_${tableViewType}`, null);
      setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
      notifications.show({
        title: 'Default table view removed',
        message: (
          <>
            Your default table view has been removed.{' '}
            <Text className="link" span onClick={() => window.location.reload()}>
              Click here
            </Text>{' '}
            to reload.
          </>
        ),
      });
      return;
    }

    const latestDefaultTableView = await getTableView(latestDefaultTableViewId);
    if (!latestDefaultTableView) {
      // [Stale cache] User's default table view has been deleted or is inaccessible.
      void deleteDefaultTableViewIdApiCall({
        url: `admin/userPreference/DEFAULT_TABLE_VIEW_ID_${tableViewType}`,
      });
      setLocalStorageWrapped(`USER_PREFERENCE_DEFAULT_TABLE_VIEW_ID_${tableViewType}`, null);
      setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
      notifications.show({
        title: 'Default table view deleted',
        message: (
          <>
            Your default table view has been deleted.{' '}
            <Text className="link" span onClick={() => window.location.reload()}>
              Click here
            </Text>{' '}
            to reload.
          </>
        ),
      });
    } else if (latestDefaultTableViewId !== cachedDefaultTableView.tableViewId) {
      // [Stale cache] User has a different default table view.
      setLocalStorageWrapped(`USER_PREFERENCE_DEFAULT_TABLE_VIEW_ID_${tableViewType}`, latestDefaultTableViewId);
      setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, latestDefaultTableView);
      notifications.show({
        title: 'Default table view changed',
        message: (
          <>
            Your default table view has changed.{' '}
            <Text className="link" span onClick={() => window.location.reload()}>
              Click here
            </Text>{' '}
            to reload.
          </>
        ),
      });
    } else if (latestDefaultTableView.gridState !== cachedDefaultTableView.gridState) {
      // [Stale cache] User's default table view has been updated.
      setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, latestDefaultTableView);
      notifications.show({
        title: 'Default table view updated',
        message: (
          <>
            Your default table view has been updated.{' '}
            <Text className="link" span onClick={() => window.location.reload()}>
              Click here
            </Text>{' '}
            to reload.
          </>
        ),
      });
    }
  }, [tableViewType, getDefaultTableViewId, getTableView, deleteDefaultTableViewIdApiCall]);

  const getInitialTableView = useCallback(async () => {
    try {
      setLoadingInitialTableView(true);
      if (tableViewType === TABLE_VIEW_TYPE.UNKNOWN) {
        return null;
      }
      const cachedDefaultTableView = getLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, null);
      // If there's no cached default table view, load it from the API and cache it.
      if (isNil(cachedDefaultTableView)) {
        const defaultTableView = await getDefaultTableView();
        setLocalStorageWrapped(`DEFAULT_TABLE_VIEW_${tableViewType}`, defaultTableView ?? null);
        return defaultTableView;
      }
      // Otherwise, return the cached default table view and update it in the background.
      void handleBackgroundUpdateCache();
      return cachedDefaultTableView;
    } finally {
      setLoadingInitialTableView(false);
    }
  }, [tableViewType, getDefaultTableView, handleBackgroundUpdateCache]);

  return { getInitialTableView, loadingInitialTableView };
};
